import {
  getPaymentInfoByUserId,
  updatePaymentInfoByPaymentInfoId,
} from "@/api/payment-info";
import Dropdown from "@/components/form/Dropdown";
import NumberInput from "@/components/form/NumberInput";
import { toast } from "@/hooks/use-toast";
import { PaymentInfoDTO } from "@/models/payment-info.model";
import React, { useEffect, useState } from "react";
import {
  bankNameOptions,
  cpfSectionOptions,
  cpfTypeOptions,
  selfHelpGroupOptions,
} from "../../onboard-page/PaymentInfoForm";
import EditButton from "@/components/buttons/EditButton";
import TextInput from "@/components/form/TextInput";
import { useCurrentUser } from "@/context/UserContext";

interface Props {
  userId: number;
}
function PaymentInfoTab({ userId }: Props) {
  const { currentUser } = useCurrentUser();
  const [paymentInfo, setPaymentInfo] = React.useState<PaymentInfoDTO>();
  const [isReadOnly, setIsReadOnly] = useState<boolean>(true);
  const [employeeCpfContributionRate, setEmployeeCpfContributionRate] =
    useState<number>(0);
  const [employerCpfContributionRate, setEmployerCpfContributionRate] =
    useState<number>(0);
  const [basicSalary, setBasicSalary] = useState<number>(0);
  const [cpfType, setCpfType] = useState<string>("");
  const [cpfSection, setCpfSection] = useState<string>("");
  const [selfHelpGroup, setSelfHelpGroup] = useState<string>("");
  const [bankName, setBankName] = useState<string>("");
  const [bankCode, setBankCode] = useState<string>("");
  const [branchCode, setBranchCode] = useState<string>("");
  const [accountNo, setAccountNo] = useState<string>("");
  const [err, setErr] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    const fetchPaymentInfo = async () => {
      if (!userId) return;
      const paymentInfo = await getPaymentInfoByUserId(userId);
      console.log("paymentInfo", paymentInfo);
      setPaymentInfo(paymentInfo);
      setBasicSalary(paymentInfo.basicSalary);
      setCpfType(paymentInfo.cpfType || "NONE");
      setEmployeeCpfContributionRate(
        paymentInfo.employeeCpfContributionRate || 0
      );
      setEmployerCpfContributionRate(
        paymentInfo.employerCpfContributionRate || 0
      );
      setCpfSection(paymentInfo.cpfSection || "NONE");
      setSelfHelpGroup(paymentInfo.selfHelpGroup || "NONE");
      setBankName(paymentInfo.bankName || "");
      setBankCode(paymentInfo.bankCode);
      setBranchCode(paymentInfo.branchCode);
      setAccountNo(paymentInfo.accountNo);
    };
    fetchPaymentInfo();
  }, [userId]);

  const handleCancel = () => {
    setErr("");
    setIsReadOnly(true);
    if (!paymentInfo) return;
    setBasicSalary(paymentInfo.basicSalary);
    setCpfType(paymentInfo.cpfType || "");
    setEmployeeCpfContributionRate(
      paymentInfo.employeeCpfContributionRate || 0
    );
    setEmployerCpfContributionRate(
      paymentInfo.employerCpfContributionRate || 0
    );
    setCpfSection(paymentInfo.cpfSection || "");
    setSelfHelpGroup(paymentInfo.selfHelpGroup || "");
    setBankName(paymentInfo.bankName || "");
    setBankCode(paymentInfo.bankCode);
    setBranchCode(paymentInfo.branchCode);
    setAccountNo(paymentInfo.accountNo);
  };

  const isNoChange = () => {
    const res =
      paymentInfo?.basicSalary === basicSalary &&
      paymentInfo?.cpfType === cpfType &&
      paymentInfo?.cpfSection === cpfSection &&
      paymentInfo?.selfHelpGroup === selfHelpGroup &&
      paymentInfo?.bankName === bankName &&
      paymentInfo?.bankCode === bankCode &&
      paymentInfo?.branchCode === branchCode &&
      paymentInfo?.accountNo === accountNo &&
      paymentInfo.employeeCpfContributionRate === employeeCpfContributionRate &&
      paymentInfo.employerCpfContributionRate === employerCpfContributionRate;
    return res;
  };

  const isValid = () => {
    if (!basicSalary || basicSalary === 0) {
      setErr("Salary must be more than 0.");
      return false;
    }
    if (
      isNaN(employeeCpfContributionRate) ||
      isNaN(employerCpfContributionRate) ||
      cpfType.length === 0 ||
      cpfSection.length === 0 ||
      selfHelpGroup.length === 0
    ) {
      setErr("Please fill in the relevant CPF fields.");
      return false;
    }
    setErr("");
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    setIsSubmitting(true);
    e.preventDefault();
    if (!isValid() || !paymentInfo) {
      setIsSubmitting(false);
      return;
    }

    try {
      const updatedPaymentInfo: PaymentInfoDTO = {
        id: paymentInfo.id,
        basicSalary,
        cpfType: cpfType === "NONE" ? undefined : cpfType,
        employeeCpfContributionRate,
        employerCpfContributionRate,
        cpfSection: cpfSection === "NONE" ? undefined : cpfSection,
        selfHelpGroup: selfHelpGroup === "NONE" ? undefined : selfHelpGroup,
        bankName,
        bankCode,
        branchCode,
        accountNo,
      };
      const res = await updatePaymentInfoByPaymentInfoId(
        updatedPaymentInfo.id,
        updatedPaymentInfo
      );
      if (res) {
        setPaymentInfo(res);
        setIsReadOnly(true);
        toast({
          title: "Payment Info Updated",
        });
      } else {
        toast({
          title: "An error occurred",
          description: "Please try again",
          variant: "destructive",
        });
      }
    } catch (e) {
      console.error(e);
      toast({
        title: "An error occurred",
        description: "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 p-5">
      <div className="flex justify-between">
        <div>
          <div className="text-lg font-bold">Payment Information</div>
          <div className="w-full flex flex-col">
            <div
              className={` text-gray-500 text-sm transition-all duration-200 h-12`}
            >
              {isReadOnly
                ? "Includes CPF and bank details."
                : "Edit information below."}
            </div>
          </div>
        </div>

        <EditButton
          handleEditing={() => setIsReadOnly(false)}
          isEditing={isReadOnly}
          handleCancel={handleCancel}
          isNoChange={isNoChange}
          handleSave={handleSubmit}
          isSubmitting={isSubmitting}
        />
      </div>
      <form className="w-full h-full flex sm:flex-row flex-col gap-6">
        <div className="w-full h-full flex flex-col gap-4">
          <div className="font-semibold">Salary and CPF</div>
          <NumberInput
            label="Basic Salary *"
            value={basicSalary}
            setValue={setBasicSalary}
            readOnly={currentUser?.role === "STAFF" || isReadOnly}
          />
          <div className="flex w-full gap-4">
            <NumberInput
              label="Employee CPF Contribution Rate (%) *"
              value={employeeCpfContributionRate}
              setValue={setEmployeeCpfContributionRate}
              readOnly={currentUser?.role === "STAFF" || isReadOnly}
            />
            <NumberInput
              label="Employer CPF Contribution Rate (%) *"
              value={employerCpfContributionRate}
              setValue={setEmployerCpfContributionRate}
              readOnly={currentUser?.role === "STAFF" || isReadOnly}
            />
          </div>

          <Dropdown
            items={cpfTypeOptions}
            label="CPF Type *"
            selectedString={cpfType}
            setSelectedString={setCpfType}
            isReadOnly={isReadOnly}
          />
          <Dropdown
            label="CPF Section *"
            items={cpfSectionOptions}
            selectedString={cpfSection}
            setSelectedString={setCpfSection}
            isReadOnly={isReadOnly}
          />
          <Dropdown
            label="Self Help Group *"
            items={selfHelpGroupOptions}
            selectedString={selfHelpGroup}
            setSelectedString={setSelfHelpGroup}
            isReadOnly={isReadOnly}
          />
        </div>

        <div className="w-full h-full flex flex-col gap-4">
          <div className="font-semibold">Bank Details</div>
          <div className="text-sm mb-2">
            Refer{" "}
            <a
              href="https://www.uob.com.sg/assets/pdfs/global/achcode.pdf"
              className="text-blue-500 underline"
              target="_blank"
              rel="noreferrer"
            >
              here
            </a>{" "}
            for more details on the respective banks.
          </div>
          <Dropdown
            items={bankNameOptions}
            label="Bank Name"
            selectedString={bankName}
            setSelectedString={setBankName}
            isReadOnly={isReadOnly}
          />
          <TextInput
            label="Bank Code"
            value={bankCode}
            setValue={setBankCode}
            type="text"
            readOnly={isReadOnly}
          />
          <TextInput
            label="Branch Code"
            value={branchCode}
            setValue={setBranchCode}
            type="text"
            readOnly={isReadOnly}
          />
          <TextInput
            label="Account Number"
            value={accountNo}
            setValue={setAccountNo}
            type="text"
            readOnly={isReadOnly}
          />
        </div>
      </form>
      {err && <div className="text-red-500 text-sm">{err}</div>}
    </div>
  );
}

export default PaymentInfoTab;
